import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type UserRole = 'user' | 'org_admin' | 'global_admin';

export type AuthUser = {
  id: number;
  email: string | null;
  name: string | null;
  avatarUrl: string | null;
  enabled: boolean;
  role: UserRole;
  // Fingerprint-keyed guest account (no real Casdoor login, zero credits).
  isAnonymous: boolean;
  // Whether the one-time email-verification promo has been claimed.
  emailPromoGranted: boolean;
};

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  // Redirects the browser to the Casdoor-hosted sign-in / sign-up page.
  // `returnTo` is the in-app path to land on after a successful callback.
  signIn: (returnTo?: string) => void;
  signUp: (returnTo?: string) => void;
  // Guest login: find-or-create the anonymous user for this browser
  // fingerprint and start a session. Resolves once `user` is refreshed.
  signInAnonymously: (fingerprint: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function startUrl(kind: 'login' | 'signup', returnTo?: string): string {
  const q = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
  return `/api/v1/auth/${kind}${q}`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const res = await fetch('/api/v1/auth/session', { credentials: 'include' });
      if (!res.ok) {
        setUser(null);
        return;
      }
      const data = (await res.json()) as { user: AuthUser };
      setUser(data.user);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const signIn = (returnTo?: string) => {
    window.location.assign(startUrl('login', returnTo));
  };
  const signUp = (returnTo?: string) => {
    window.location.assign(startUrl('signup', returnTo));
  };

  const signInAnonymously = async (fingerprint: string) => {
    const res = await fetch('/api/v1/auth/anonymous', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fingerprint }),
    });
    if (!res.ok) throw new Error(`anonymous sign-in failed (${res.status})`);
    await refresh();
  };

  // Top-level navigation so the response can 302 the browser through
  // Casdoor's `/api/logout` (clearing its session cookie on its own origin)
  // before landing back on our marketing page. A fetch() can't follow that
  // cross-origin chain.
  const logout = async () => {
    window.location.assign('/api/v1/auth/logout');
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signInAnonymously, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
